import React from 'react';
import axiosInstance from '../../../apiServices/axios';
import { IRL_DOC } from '../../../apiServices/api_routes';
import { parseHierarchicalKeywordLabels } from '../../../utils/dataroomKeywords';
import { VDRDocumentIcon } from '../components/VDRItemIcons';
import styles from '../css/vdr.module.css';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const AI_ORIGIN_DISCLAIMER =
    'AI-origin analysis is probabilistic and indicates characteristics consistent with AI-assisted content. It does not establish authorship.';

function keywordLabel(item) {
    if (typeof item === 'string') {
        const trimmed = item.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
                return keywordLabel(JSON.parse(trimmed)) || trimmed;
            } catch (error) {
                return trimmed;
            }
        }
        return trimmed;
    }
    if (item && typeof item === 'object' && !Array.isArray(item)) {
        const raw = item.name || item.keyword;
        if (typeof raw === 'string') return keywordLabel(raw);
    }
    return '';
}

function fileKeywordLabels(file) {
    const raw = file?.keywords;
    const items = Array.isArray(raw) ? raw : parseHierarchicalKeywordLabels(raw);
    return [...new Set(items.map(keywordLabel).filter(Boolean))];
}

function fileName(file) {
    return file.document_name || file.name || file.metadata_storage_name || 'Untitled';
}

function fileExtension(file) {
    const name = fileName(file);
    const explicit = file.extension;
    if (explicit) return String(explicit).toLowerCase();
    const parts = String(name).split('?')[0].split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
}

function parseDate(value) {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

function monthKey(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(key) {
    const [year, month] = key.split('-');
    return `${MONTHS[Number(month) - 1]} ${year}`;
}

function weekStart(date) {
    const d = new Date(date);
    const diff = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - diff);
    d.setHours(0, 0, 0, 0);
    return d;
}

function weekKey(date) {
    const start = weekStart(date);
    return `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
}

function weekAxisLabel(key) {
    const [year, month, day] = key.split('-').map(Number);
    const start = new Date(year, month - 1, day);
    return start.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

function weekTooltipTitle(key) {
    const [year, month, day] = key.split('-').map(Number);
    const start = new Date(year, month - 1, day);
    return `Week of ${start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

const AI_LABELS = {
    LIKELY_AI_ASSISTED: 'Likely AI-assisted',
    NO_MATERIAL_AI_SIGNAL: 'No material AI signal',
    INCONCLUSIVE: 'Inconclusive',
    NOT_ANALYSED: 'Not analysed',
    PROCESSING: 'Processing',
};

function aiRecord(file) {
    const status = file?.ai_origin_status || 'NOT_ANALYSED';
    const classification = file?.ai_origin_classification || '';
    const analysed = (status === 'ANALYSED' || status === 'PARTIAL') && Boolean(classification);
    return {
        status,
        classification,
        analysed,
        likely: analysed && classification === 'LIKELY_AI_ASSISTED',
        other: analysed && classification === 'NO_MATERIAL_AI_SIGNAL',
        coverage: typeof file?.ai_origin_coverage === 'number' ? file.ai_origin_coverage : null,
        units: Number(file?.ai_origin_analysable_units) || 0,
    };
}

function aiLabel(file) {
    const record = aiRecord(file);
    if (record.status === 'PROCESSING') return 'Processing';
    if (!record.analysed) return 'Not analysed';
    return AI_LABELS[record.classification] || 'Not analysed';
}

function weightedCoverage(files) {
    let units = 0;
    let aiUnits = 0;
    files.forEach(file => {
        const record = aiRecord(file);
        if (!record.analysed || !record.units || record.coverage == null) return;
        units += record.units;
        aiUnits += (record.coverage / 100) * record.units;
    });
    if (!units) return null;
    return Math.round((100 * aiUnits) / units);
}

function scopeSummary(files) {
    const records = files.map(aiRecord);
    const analysed = records.filter(record => record.analysed);
    let classification = 'Not analysed';
    if (!analysed.length) {
        classification = records.some(record => record.status === 'PROCESSING') ? 'Processing' : 'Not analysed';
    } else {
        const labels = new Set(analysed.map(record => record.classification));
        classification = labels.size === 1 ? AI_LABELS[analysed[0].classification] || 'Inconclusive' : 'Inconclusive';
    }
    const coveragePct = weightedCoverage(files);
    const confidences = [...new Set(files.filter(file => aiRecord(file).analysed).map(fileConfidence).filter(Boolean))];
    const inconclusive = records.filter(record => record.classification === 'INCONCLUSIVE').length;
    const notAnalysed = records.filter(record => !record.analysed && record.status !== 'PROCESSING').length;
    return {
        classification,
        coverage: coveragePct == null ? '—' : `${coveragePct}%`,
        coveragePct,
        confidence: confidences.length === 1 ? confidences[0] : '—',
        analysedCount: analysed.length,
        totalCount: files.length,
        inconclusive,
        notAnalysed,
    };
}

function aiLabelTone(file) {
    const record = aiRecord(file);
    if (record.status === 'PROCESSING') return styles.analyticsAiMuted;
    if (!record.analysed) return styles.analyticsAiFaint;
    if (record.classification === 'NO_MATERIAL_AI_SIGNAL') return styles.analyticsAiMuted;
    if (record.classification === 'INCONCLUSIVE') return styles.analyticsAiFaint;
    return '';
}

function confidenceTone(confidence) {
    const value = String(confidence || '').toLowerCase();
    if (value === 'low') return styles.analyticsAiFaint;
    if (value === 'medium') return styles.analyticsAiMuted;
    return '';
}

function AiOriginInfoIcon() {
    return (
        <span className={styles.analyticsInfoIcon} title={AI_ORIGIN_DISCLAIMER} aria-label={AI_ORIGIN_DISCLAIMER}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
                <circle cx="6.5" cy="6.5" r="6" stroke="#c6c8d2" />
                <path d="M6.5 6V9" stroke="#989bad" strokeWidth="1.2" strokeLinecap="round" />
                <circle cx="6.5" cy="4.25" r="0.65" fill="#989bad" />
            </svg>
        </span>
    );
}

function AISummaryStrip({ summary }) {
    const coverageSuffix = summary.coveragePct == null ? null : 'of analysed content';
    const breakdown =
        summary.inconclusive || summary.notAnalysed
            ? `${summary.inconclusive} inconclusive · ${summary.notAnalysed} not analysed`
            : '';
    return (
        <div className={styles.analyticsSummary}>
            <div className={styles.analyticsSummaryCol}>
                <p className={styles.analyticsSummaryLabel}>AI-origin analysis</p>
                <p className={styles.analyticsSummaryValueMedium}>{summary.classification}</p>
            </div>
            <div className={styles.analyticsSummaryColWide}>
                <p className={styles.analyticsSummaryLabel}>Est. AI-origin coverage</p>
                <p className={styles.analyticsSummaryCoverage}>
                    <span className={styles.analyticsSummaryValueMedium}>{summary.coverage}</span>
                    {coverageSuffix ? <span className={styles.analyticsSummaryMuted}> {coverageSuffix}</span> : null}
                </p>
            </div>
            <div className={styles.analyticsSummaryCol}>
                <p className={styles.analyticsSummaryLabel}>Confidence</p>
                <p className={`${styles.analyticsSummaryValueMedium} ${confidenceTone(summary.confidence)}`}>{summary.confidence}</p>
            </div>
            <div className={styles.analyticsSummaryColCoverage}>
                <p className={styles.analyticsSummaryLabel}>Analysis coverage</p>
                <p className={styles.analyticsSummaryCoverage}>
                    <span className={styles.analyticsSummaryValueMedium}>{summary.analysedCount} of {summary.totalCount} </span>
                    <span className={styles.analyticsSummaryMuted}>documents analysed</span>
                </p>
                {breakdown ? <p className={styles.analyticsSummaryBreakdown}>{breakdown}</p> : null}
            </div>
        </div>
    );
}

function ScopeTableHeader({ firstColumn = '' }) {
    return (
        <div className={`${styles.analyticsTableHead} ${styles.analyticsTableSource}`}>
            <span>{firstColumn}</span>
            <span>Analysis</span>
            <span>Est. AI-origin coverage</span>
            <span>Analysis coverage</span>
        </div>
    );
}

function fileConfidence(file) {
    const record = aiRecord(file);
    if (!record.analysed) return '';
    const value = String(file?.ai_origin_confidence || '').toLowerCase();
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function seriesFrom(files, dateOf) {
    const counts = new Map();
    files.forEach(file => {
        const date = dateOf(file);
        if (!date) return;
        const key = monthKey(date);
        counts.set(key, (counts.get(key) || 0) + 1);
    });
    return [...counts.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .slice(-12)
        .map(([key, count]) => ({ key, label: monthLabel(key), count }));
}

function tooltipStyle(index, count, barHeightPercent, stacked) {
    const percent = ((index + 0.5) / count) * 100;
    const plot = 116;
    const tipHeight = stacked ? 132 : 56;
    const barTop = plot * (1 - barHeightPercent / 100);
    const top = Math.max(0, barTop - tipHeight - 8);
    const horizontal = percent > 70 ? { right: 0, left: 'auto' } : percent < 24 ? { left: 0 } : { left: `calc(${percent}% - 95px)` };
    return { top, ...horizontal };
}

function BarChart({ series, stacked = false, pending = false, hover, onHover, emptyCopy }) {
    if (!series.length) {
        return <p className={styles.analyticsEmpty}>{emptyCopy || 'No dated documents in this view.'}</p>;
    }
    const max = Math.max(...series.map(item => item.count), 1);
    const activeIndex = hover ? series.findIndex(item => item.key === hover.key) : -1;
    return (
        <div className={styles.analyticsChart}>
            <div className={styles.analyticsBars}>
                {series.map(item => {
                    const height = Math.max(6, Math.round((item.count / max) * 100));
                    const aiShare = stacked && item.count ? Math.round(((item.ai || 0) / item.count) * 100) : 0;
                    const otherShare = stacked && item.count ? Math.round(((item.other || 0) / item.count) * 100) : 0;
                    return (
                        <button
                            key={item.key}
                            type="button"
                            className={`${styles.analyticsBarHit} ${hover?.key === item.key ? styles.analyticsBarHitActive : ''}`}
                            onMouseEnter={() => onHover?.(item)}
                            onMouseLeave={() => onHover?.(null)}
                            onFocus={() => onHover?.(item)}
                            onBlur={() => onHover?.(null)}
                            aria-label={`${item.label}: ${item.count}`}>
                            <span
                                className={`${styles.analyticsBar} ${pending ? styles.analyticsBarPending : stacked ? styles.analyticsBarStacked : styles.analyticsBarSolid}`}
                                style={{ height: `${height}%` }}>
                                {stacked ? <span className={styles.analyticsBarOther} style={{ height: `${otherShare}%` }} /> : null}
                                {stacked ? <span className={styles.analyticsBarAi} style={{ height: `${aiShare}%`, bottom: `${otherShare}%` }} /> : null}
                            </span>
                        </button>
                    );
                })}
            </div>
            {hover && activeIndex >= 0 ? (
                <div
                    className={`${styles.analyticsTooltip} ${stacked && !pending ? styles.analyticsTooltipRich : ''}`}
                    style={tooltipStyle(activeIndex, series.length, Math.max(6, Math.round((hover.count / max) * 100)), stacked || pending)}>
                    <p className={styles.analyticsTooltipTitle}>{hover.tooltipTitle || hover.label}</p>
                    {stacked && !pending ? (
                        <>
                            <div className={styles.analyticsTooltipGrid}>
                                <div>
                                    <p className={styles.analyticsTooltipMetricLabel}>Documents created</p>
                                    <p className={styles.analyticsTooltipMetricValue}>{hover.count}</p>
                                </div>
                                <div>
                                    <p className={styles.analyticsTooltipMetricLabel}>Likely AI-assisted</p>
                                    <p className={styles.analyticsTooltipMetricValueAi}>{hover.ai || 0}</p>
                                </div>
                            </div>
                            <div className={styles.analyticsTooltipDivider}>
                                <p className={styles.analyticsTooltipMetricLabel}>Est. AI-origin coverage</p>
                                <p className={styles.analyticsTooltipMetricValue}>{hover.coverageLabel || '—'}</p>
                            </div>
                        </>
                    ) : (
                        <p className={styles.analyticsTooltipLine}>
                            {hover.chart === 'upload' ? 'Documents uploaded' : 'Documents created'}{' '}
                            <strong>{hover.count}</strong>
                        </p>
                    )}
                    {pending ? (
                        <>
                            <p className={styles.analyticsTooltipLine}>Analysis: Not analysed</p>
                            <p className={styles.analyticsTooltipLine}>Est. AI-origin coverage —</p>
                        </>
                    ) : null}
                </div>
            ) : null}
            <div className={styles.analyticsAxis}>
                <span>{series[0].label}</span>
                <span>{series[series.length - 1].label}</span>
            </div>
        </div>
    );
}

function descendantFolderIds(rootId, folders) {
    const ids = new Set();
    if (rootId == null) return ids;
    ids.add(rootId);
    let grown = true;
    while (grown) {
        grown = false;
        (folders || []).forEach(folder => {
            const parent = folder.parent ?? folder.parent_id;
            if (parent != null && ids.has(parent) && !ids.has(folder.id)) {
                ids.add(folder.id);
                grown = true;
            }
        });
    }
    return ids;
}

function folderIdOf(file) {
    return file?.ws_folder_id ?? file?.folder_id ?? file?.folder;
}

function analyticsBaseDocs(allDocs, scope, folders, workstreamId) {
    const type = scope?.type || 'project';
    const scopeWsId = scope?.workstreamId != null ? String(scope.workstreamId) : null;
    const selected = workstreamId === 'all' ? 'all' : String(workstreamId);

    if (selected === 'initial') {
        return allDocs.filter(file => file.isInitial);
    }

    if (selected === 'all') {
        if (type === 'initial') return allDocs.filter(file => file.isInitial);
        if (type === 'folder' && scope.folderId != null) {
            const allowedFolders = descendantFolderIds(scope.folderId, folders);
            return allDocs.filter(
                file =>
                    !file.isInitial &&
                    (!scopeWsId || String(file.workstream_id) === scopeWsId) &&
                    allowedFolders.has(folderIdOf(file)),
            );
        }
        return allDocs.filter(file => !file.isInitial);
    }

    let docs = allDocs.filter(file => !file.isInitial && String(file.workstream_id) === selected);

    if (type === 'folder' && scope.folderId != null && scopeWsId === selected) {
        const allowedFolders = descendantFolderIds(scope.folderId, folders);
        docs = docs.filter(file => allowedFolders.has(folderIdOf(file)));
    }

    return docs;
}

function scopeHeading(scope) {
    if (!scope || scope.type === 'project') return '';
    if (scope.type === 'initial') return 'Initial Project Documents';
    if (scope.type === 'folder') {
        if (scope.workstreamName && scope.folderName) {
            return `${scope.workstreamName} › ${scope.folderName}`;
        }
        return scope.folderName || scope.workstreamName || '';
    }
    return scope.workstreamName || 'Workstream';
}

function RankList({ rows, empty, onSelect }) {
    if (!rows.length) return <p className={styles.analyticsEmpty}>{empty}</p>;
    return (
        <div>
            {rows.map(row => {
                const body = (
                    <>
                        <span>{row.label}</span>
                        <span className={styles.analyticsRankCount}>{row.count}</span>
                    </>
                );
                if (!onSelect) {
                    return (
                        <div key={row.label} className={styles.analyticsRankRow}>
                            {body}
                        </div>
                    );
                }
                return (
                    <button key={row.label} type="button" className={styles.analyticsRankRow} onClick={() => onSelect(row)}>
                        {body}
                    </button>
                );
            })}
        </div>
    );
}

const VDRAnalyticsView = ({
    workstreams = [],
    folders = [],
    projectFiles = [],
    projectUsersAll = [],
    scope = { type: 'project' },
    onOpenFile,
}) => {
    const [documents, setDocuments] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [workstreamId, setWorkstreamId] = React.useState('all');
    const [fileType, setFileType] = React.useState('all');
    const [year, setYear] = React.useState('all');
    const [creationMode, setCreationMode] = React.useState('documents');
    const [hoverBar, setHoverBar] = React.useState(null);
    const [drill, setDrill] = React.useState(null);
    const [selectedKeyword, setSelectedKeyword] = React.useState(null);
    const [selectedAuthor, setSelectedAuthor] = React.useState(null);
    const [selectedReader, setSelectedReader] = React.useState(null);
    const [manageKeywords, setManageKeywords] = React.useState(false);

    React.useEffect(() => {
        let cancelled = false;
        const ids = (workstreams || []).map(ws => ws.id).filter(Boolean);
        if (!ids.length) {
            setDocuments([]);
            return undefined;
        }
        setLoading(true);
        Promise.all(
            ids.map(id => {
                const ws = (workstreams || []).find(row => String(row.id) === String(id));
                return axiosInstance
                    .get(`${IRL_DOC}${localStorage.project_id}/${id}`)
                    .then(response => {
                        const payload = response.data;
                        const rows = Array.isArray(payload) ? payload : Array.isArray(payload?.documents) ? payload.documents : [];
                        return rows.map(file => ({
                            ...file,
                            workstream_id: file.workstream_id ?? id,
                            workstream: file.workstream || ws?.name,
                        }));
                    })
                    .catch(() => []);
            }),
        )
            .then(lists => {
                if (cancelled) return;
                const byId = new Map();
                lists.flat().forEach(file => {
                    if (file?.id != null) byId.set(String(file.id), file);
                });
                setDocuments([...byId.values()]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [workstreams]);

    const initialDocs = React.useMemo(
        () =>
            (projectFiles || []).map(file => ({
                ...file,
                document_name: file.document_name,
                workstream: 'Initial Project Documents',
                workstream_id: 'initial',
                isInitial: true,
            })),
        [projectFiles],
    );

    const allDocs = React.useMemo(() => [...initialDocs, ...documents], [initialDocs, documents]);

    React.useEffect(() => {
        setDrill(null);
    }, [scope?.type, scope?.workstreamId, scope?.folderId, workstreamId]);

    const baseDocs = React.useMemo(
        () => analyticsBaseDocs(allDocs, scope, folders, workstreamId),
        [allDocs, scope, folders, workstreamId],
    );

    const scopeLocked = scope?.type && scope.type !== 'project';
    const scopeTitle = scopeHeading(scope);

    const displayScopeTitle = React.useMemo(() => {
        if (workstreamId === 'initial') return 'Initial Project Documents';
        if (workstreamId === 'all') {
            if (scope?.type === 'folder') return scopeTitle;
            return scope?.type === 'workstream' ? '' : scopeTitle;
        }
        const ws = (workstreams || []).find(row => String(row.id) === String(workstreamId));
        if (scope?.type === 'folder' && String(scope.workstreamId) === String(workstreamId)) {
            return scopeTitle;
        }
        return ws?.name || '';
    }, [workstreamId, workstreams, scope, scopeTitle]);

    const fileTypes = React.useMemo(() => {
        const types = new Set(baseDocs.map(fileExtension).filter(Boolean));
        return [...types].sort();
    }, [baseDocs]);

    const years = React.useMemo(() => {
        const values = new Set();
        baseDocs.forEach(file => {
            const uploaded = parseDate(file.created || file.upload_date || file.uploaded_at);
            const created = parseDate(file.metadata_creation_date || file.document_date);
            if (uploaded) values.add(String(uploaded.getFullYear()));
            if (created) values.add(String(created.getFullYear()));
        });
        return [...values].sort();
    }, [baseDocs]);

    React.useEffect(() => {
        if (scopeLocked) {
            if (scope.type === 'initial') setWorkstreamId('initial');
            else if (scope.workstreamId != null) setWorkstreamId(String(scope.workstreamId));
            return;
        }
        setWorkstreamId('all');
    }, [scope?.type, scope?.workstreamId, scopeLocked]);

    const filtered = React.useMemo(() => {
        return baseDocs.filter(file => {
            if (fileType !== 'all' && fileExtension(file) !== fileType) return false;
            if (year !== 'all') {
                const uploaded = parseDate(file.created || file.upload_date || file.uploaded_at);
                const created = parseDate(file.metadata_creation_date || file.document_date);
                const yearsOnFile = [uploaded, created].filter(Boolean).map(date => String(date.getFullYear()));
                if (!yearsOnFile.includes(year)) return false;
            }
            return true;
        });
    }, [baseDocs, fileType, year]);

    const uploadSeries = React.useMemo(
        () => seriesFrom(filtered, file => parseDate(file.created || file.upload_date || file.uploaded_at)),
        [filtered],
    );
    const creationSeries = React.useMemo(() => {
        const dated = [];
        const buckets = new Map();
        filtered.forEach(file => {
            const date = parseDate(file.metadata_creation_date || file.document_date);
            if (!date) return;
            dated.push(file);
            const key = weekKey(date);
            const current = buckets.get(key) || { key, files: [] };
            current.files.push(file);
            buckets.set(key, current);
        });
        const analysedAnywhere = dated.some(file => aiRecord(file).analysed);
        return {
            analysedAnywhere,
            points: [...buckets.entries()]
                .sort((a, b) => a[0].localeCompare(b[0]))
                .slice(-12)
                .map(([key, bucket]) => {
                    const records = bucket.files.map(aiRecord);
                    const coverage = weightedCoverage(bucket.files);
                    return {
                        key,
                        label: weekAxisLabel(key),
                        tooltipTitle: weekTooltipTitle(key),
                        count: bucket.files.length,
                        ai: records.filter(record => record.likely).length,
                        other: records.filter(record => record.other).length,
                        coverageLabel: coverage == null ? '—' : `${coverage}%`,
                    };
                }),
        };
    }, [filtered]);

    const readerName = id => {
        const row = (projectUsersAll || []).find(item => String(item?.value?.group_member_id) === String(id));
        return row?.value?.full_name || 'Unknown';
    };

    const readStats = React.useMemo(() => {
        const readerCounts = new Map();
        let reads = 0;
        filtered.forEach(file => {
            const ids = Array.isArray(file.read_by) ? file.read_by : [];
            reads += ids.length;
            ids.forEach(id => readerCounts.set(id, (readerCounts.get(id) || 0) + 1));
        });
        const mostRead = [...filtered]
            .map(file => ({ label: fileName(file), count: Array.isArray(file.read_by) ? file.read_by.length : 0, file }))
            .filter(row => row.count > 0)
            .sort((a, b) => b.count - a.count)
            .slice(0, 3);
        const activeReaders = [...readerCounts.entries()]
            .map(([id, count]) => ({ label: readerName(id), count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 3);
        return { files: filtered.length, reads, readers: readerCounts.size, mostRead, activeReaders };
    }, [filtered, projectUsersAll]);

    const authors = React.useMemo(() => {
        const counts = new Map();
        let unknown = 0;
        filtered.forEach(file => {
            const author = String(file.metadata_author || '').trim();
            if (!author || author === 'No Name') {
                unknown += 1;
                return;
            }
            counts.set(author, (counts.get(author) || 0) + 1);
        });
        const rows = [...counts.entries()]
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 8);
        if (unknown) rows.push({ label: 'Unknown', count: unknown });
        return rows;
    }, [filtered]);

    const keywords = React.useMemo(() => {
        const counts = new Map();
        filtered.forEach(file => {
            fileKeywordLabels(file).forEach(label => {
                const key = String(label).trim();
                if (!key) return;
                counts.set(key, (counts.get(key) || 0) + 1);
            });
        });
        const rows = [...counts.entries()]
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 8);
        const max = rows[0]?.count || 1;
        return rows.map(row => ({ ...row, width: Math.round((row.count / max) * 100) }));
    }, [filtered]);

    const sourceRows = React.useMemo(() => {
        const groups = new Map();
        filtered.forEach(file => {
            const key = file.isInitial ? 'initial' : String(file.workstream_id || file.workstream || 'unknown');
            const label = file.isInitial ? 'Initial Project Documents' : file.workstream || 'Workstream';
            const current = groups.get(key) || { key, label, count: 0, files: [] };
            current.count += 1;
            current.files.push(file);
            groups.set(key, current);
        });
        const initial = groups.get('initial');
        const rest = [...groups.entries()]
            .filter(([key]) => key !== 'initial')
            .map(([, value]) => value)
            .sort((a, b) => b.count - a.count);
        return [...(initial ? [initial] : []), ...rest];
    }, [filtered]);

    const originStats = React.useMemo(() => scopeSummary(filtered), [filtered]);
    const showScopedFileTable =
        (scope?.type === 'initial' && workstreamId === 'initial') ||
        (scope?.type === 'folder' &&
            workstreamId !== 'all' &&
            String(scope.workstreamId) === String(workstreamId));

    return (
        <div className={styles.analyticsPage}>
            <div className={styles.analyticsHeader}>
                <div>
                    <h2 className={styles.analyticsTitle}>VDR Analytics</h2>
                    {displayScopeTitle ? <p className={styles.analyticsScopeTitle}>{displayScopeTitle}</p> : null}
                    <p className={styles.analyticsSubtitle}>Metadata and preparation patterns across accessible VDR evidence.</p>
                    <p className={styles.analyticsNote}>Project Mail and Meeting Recordings are excluded from document preparation analytics.</p>
                </div>
                <div className={styles.analyticsFilters}>
                    <select className={styles.analyticsSelect} value={workstreamId} onChange={event => setWorkstreamId(event.target.value)} aria-label="Workstream">
                        <option value="all">All workstreams</option>
                        <option value="initial">Initial Project Documents</option>
                        {(workstreams || []).map(ws => (
                            <option key={ws.id} value={ws.id}>
                                {ws.name}
                            </option>
                        ))}
                    </select>
                    <select className={styles.analyticsSelect} value={fileType} onChange={event => setFileType(event.target.value)} aria-label="File type">
                        <option value="all">All file types</option>
                        {fileTypes.map(type => (
                            <option key={type} value={type}>
                                {type.toUpperCase()}
                            </option>
                        ))}
                    </select>
                    <select className={styles.analyticsSelect} value={year} onChange={event => setYear(event.target.value)} aria-label="Year">
                        <option value="all">All years</option>
                        {years.map(value => (
                            <option key={value} value={value}>
                                {value}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {loading ? <p className={styles.analyticsEmpty}>Loading documents…</p> : null}

            <div className={styles.analyticsChartRow}>
                <section className={styles.analyticsCard}>
                    <h3 className={styles.analyticsCardTitle}>Upload activity</h3>
                    <p className={styles.analyticsCardHint}>Based on Lens upload timestamp</p>
                    <BarChart series={uploadSeries} hover={hoverBar?.chart === 'upload' ? hoverBar : null} onHover={item => setHoverBar(item ? { ...item, chart: 'upload' } : null)} />
                </section>
                <section className={styles.analyticsCard}>
                    <div className={styles.analyticsCardHead}>
                        <h3 className={styles.analyticsCardTitle}>Document creation activity</h3>
                        <div className={styles.analyticsToggle}>
                            <button
                                type="button"
                                className={
                                    creationMode === 'documents' ? styles.analyticsToggleActive : styles.analyticsToggleInactive
                                }
                                onClick={() => {
                                    setCreationMode('documents');
                                    setHoverBar(null);
                                }}>
                                Document activity
                            </button>
                            <button
                                type="button"
                                className={
                                    creationMode === 'ai' ? styles.analyticsToggleActiveAi : styles.analyticsToggleInactive
                                }
                                onClick={() => {
                                    setCreationMode('ai');
                                    setHoverBar(null);
                                }}>
                                AI-origin
                            </button>
                        </div>
                    </div>
                    <p className={styles.analyticsCardHint}>
                        {creationMode === 'documents'
                            ? 'Based on embedded document creation timestamp'
                            : 'AI-origin analysis by embedded document creation date'}
                    </p>
                    {creationMode === 'ai' && creationSeries.analysedAnywhere ? (
                        <div className={styles.analyticsLegend}>
                            <span><i className={styles.analyticsSwatchOther} /> Other created</span>
                            <span><i className={styles.analyticsSwatchAi} /> Likely AI-assisted</span>
                        </div>
                    ) : null}
                    <BarChart
                        series={creationSeries.points}
                        pending={creationMode === 'ai' && !creationSeries.analysedAnywhere}
                        stacked={creationMode === 'ai' && creationSeries.analysedAnywhere}
                        hover={hoverBar?.chart === 'created' ? hoverBar : null}
                        onHover={item => setHoverBar(item ? { ...item, chart: 'created' } : null)}
                        emptyCopy={creationMode === 'ai' ? 'No creation-date data. Documents without a usable embedded creation date are excluded.' : undefined}
                    />
                    {creationMode === 'ai' ? (
                        <p className={styles.analyticsFootnoteItalic}>
                            Documents without a usable embedded creation date are excluded.
                        </p>
                    ) : null}
                </section>
            </div>

            <section className={styles.analyticsCard}>
                <div className={styles.analyticsCardHead}>
                    <div className={styles.analyticsCardTitleRow}>
                        <h3 className={styles.analyticsCardTitle}>AI-origin analysis</h3>
                        <AiOriginInfoIcon />
                    </div>
                </div>
                <p className={styles.analyticsCardHint}>Estimated AI-assisted content across analysable VDR documents.</p>
                {!drill || showScopedFileTable ? <AISummaryStrip summary={originStats} /> : null}
                {drill ? (
                    <DocumentDrill
                        drill={drill}
                        onBack={() => {
                            const chain = drill.chain || [];
                            setDrill(chain.length ? { ...drill, chain: chain.slice(0, -1) } : null);
                        }}
                        onClose={() => setDrill(null)}
                        onOpenFolder={folder => setDrill({ ...drill, chain: [...(drill.chain || []), folder] })}
                        onChain={chain => setDrill({ ...drill, chain })}
                        onOpenFile={onOpenFile}
                    />
                ) : showScopedFileTable ? (
                    <ScopedFilesTable files={filtered} onOpenFile={onOpenFile} />
                ) : (
                    <div className={styles.analyticsTable}>
                        {sourceRows.some(row => row.key === 'initial') ? (
                            <>
                                <p className={styles.analyticsSectionHeading}>Project sources</p>
                                <ScopeTableHeader />
                                {sourceRows
                                    .filter(row => row.key === 'initial')
                                    .map(row => (
                                        <SourceRow
                                            key={row.label}
                                            row={row}
                                            summary={scopeSummary(row.files)}
                                            onClick={() => setDrill({ ...row, chain: [] })}
                                        />
                                    ))}
                            </>
                        ) : null}
                        {sourceRows.some(row => row.key !== 'initial') ? (
                            <>
                                <p className={styles.analyticsSectionHeading}>Workstreams</p>
                                <ScopeTableHeader firstColumn="Workstream" />
                                {sourceRows
                                    .filter(row => row.key !== 'initial')
                                    .map(row => (
                                        <SourceRow
                                            key={row.label}
                                            row={row}
                                            summary={scopeSummary(row.files)}
                                            onClick={() => setDrill({ ...row, chain: [] })}
                                        />
                                    ))}
                            </>
                        ) : null}
                        {sourceRows.length === 0 ? <p className={styles.analyticsEmpty}>No documents in this view.</p> : null}
                    </div>
                )}
                <p className={styles.analyticsFootnote}>{AI_ORIGIN_DISCLAIMER}</p>
            </section>

            <section className={styles.analyticsCard}>
                <h3 className={styles.analyticsCardTitle}>File read activity</h3>
                <div className={styles.analyticsStats}>
                    <div>
                        <p className={styles.analyticsStatValue}>{readStats.files} files</p>
                        <p className={styles.analyticsStatLabel}>total</p>
                    </div>
                    <div>
                        <p className={styles.analyticsStatValue}>{readStats.reads} reads</p>
                        <p className={styles.analyticsStatLabel}>total</p>
                    </div>
                    <div>
                        <p className={styles.analyticsStatValue}>{readStats.readers} readers</p>
                        <p className={styles.analyticsStatLabel}>unique</p>
                    </div>
                </div>
                <div className={styles.analyticsSplit}>
                    <div>
                        <p className={styles.analyticsKicker}>Most read files</p>
                        <RankList rows={readStats.mostRead} empty="No reads recorded" onSelect={row => onOpenFile?.(row.file)} />
                    </div>
                    <div>
                        <p className={styles.analyticsKicker}>Most active readers</p>
                        {selectedReader ? (
                            <KeywordPanel
                                title={selectedReader}
                                subtitle={`${readStats.activeReaders.find(row => row.label === selectedReader)?.count || 0} reads`}
                                files={filtered.filter(file =>
                                    (Array.isArray(file.read_by) ? file.read_by : []).some(id => readerName(id) === selectedReader),
                                )}
                                onClose={() => setSelectedReader(null)}
                                onOpenFile={onOpenFile}
                            />
                        ) : (
                            <RankList rows={readStats.activeReaders} empty="No readers recorded" onSelect={row => setSelectedReader(row.label)} />
                        )}
                    </div>
                </div>
            </section>

            <div className={styles.analyticsChartRow}>
                <section className={styles.analyticsCard}>
                    <h3 className={styles.analyticsCardTitle}>Document authors</h3>
                    {selectedAuthor ? (
                        <KeywordPanel
                            title={selectedAuthor}
                            subtitle={`${authors.find(row => row.label === selectedAuthor)?.count || 0} documents`}
                            files={filtered.filter(file => (selectedAuthor === 'Unknown' ? !String(file.metadata_author || '').trim() || file.metadata_author === 'No Name' : file.metadata_author === selectedAuthor))}
                            onClose={() => setSelectedAuthor(null)}
                            onOpenFile={onOpenFile}
                            searchable
                        />
                    ) : (
                        <div>
                            {authors.length === 0 ? (
                                <p className={styles.analyticsEmpty}>No author metadata on these documents</p>
                            ) : (
                                authors.map(row => (
                                    <button key={row.label} type="button" className={styles.analyticsRankRow} onClick={() => setSelectedAuthor(row.label)}>
                                        <span>{row.label}</span>
                                        <span className={styles.analyticsRankCount}>{row.count}</span>
                                    </button>
                                ))
                            )}
                        </div>
                    )}
                </section>
                <section className={styles.analyticsCard}>
                    <div className={styles.analyticsCardHead}>
                        <h3 className={styles.analyticsCardTitle}>Keyword usage</h3>
                        <button type="button" className={styles.textLink} onClick={() => setManageKeywords(true)}>
                            Manage
                        </button>
                    </div>
                    <p className={styles.analyticsCardHint}>Documents tagged across accessible VDR documents. Occurrence totals are not stored.</p>
                    {manageKeywords ? (
                        <div className={styles.analyticsKeywordPanel}>
                            <p>Keywords on this page are derived from indexed documents. There is no separate keyword catalogue to add, remove, or reset.</p>
                            <button type="button" className={styles.textLink} onClick={() => setManageKeywords(false)}>
                                Close
                            </button>
                        </div>
                    ) : selectedKeyword ? (
                        <KeywordPanel
                            title={`Keyword: ${selectedKeyword}`}
                            subtitle={`${keywords.find(row => row.label === selectedKeyword)?.count || 0} documents`}
                            files={filtered.filter(file => fileKeywordLabels(file).includes(selectedKeyword))}
                            onClose={() => setSelectedKeyword(null)}
                            onOpenFile={onOpenFile}
                        />
                    ) : keywords.length === 0 ? (
                        <p className={styles.analyticsEmpty}>No keywords on these documents</p>
                    ) : (
                        keywords.map(row => (
                            <button key={row.label} type="button" className={styles.analyticsKeywordRow} onClick={() => setSelectedKeyword(row.label)}>
                                <span className={styles.analyticsKeyword}>{row.label}</span>
                                <span className={styles.analyticsKeywordTrack}>
                                    <span className={styles.analyticsKeywordFill} style={{ width: `${row.width}%` }} />
                                </span>
                                <span className={styles.analyticsRankCount}>{row.count}</span>
                            </button>
                        ))
                    )}
                </section>
            </div>
        </div>
    );
};

function createdLabel(file) {
    const date = parseDate(file.metadata_creation_date || file.document_date);
    if (!date) return '';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fileLocation(file) {
    const folder = file.ws_folder || file.folder_name;
    const parts = [file.workstream, file.parent_folder, folder].filter((part, index, list) => part && list.indexOf(part) === index);
    return parts.join(' → ') || 'VDR';
}

function nodeCount(node) {
    return node.files.length + node.children.reduce((sum, child) => sum + nodeCount(child), 0);
}

function buildFolderNodes(files) {
    const nodes = new Map();
    const ensure = (key, label) => {
        if (!nodes.has(key)) nodes.set(key, { key, label, files: [], children: [] });
        return nodes.get(key);
    };
    files.forEach(file => {
        const folder = file.ws_folder || file.folder_name || (file.isInitial ? 'Initial Project Documents' : 'Unfiled');
        const parent = file.parent_folder && file.parent_folder !== folder ? file.parent_folder : null;
        if (parent) {
            const parentNode = ensure(parent, parent);
            let child = parentNode.children.find(item => item.label === folder);
            if (!child) {
                child = { key: `${parent}/${folder}`, label: folder, files: [], children: [] };
                parentNode.children.push(child);
            }
            child.files.push(file);
        } else {
            ensure(folder, folder).files.push(file);
        }
    });
    return [...nodes.values()].sort((a, b) => nodeCount(b) - nodeCount(a));
}

function ScopedFilesTable({ files, onOpenFile }) {
    if (!files.length) {
        return <p className={styles.analyticsEmpty}>No documents in this view.</p>;
    }
    return (
        <div className={styles.analyticsTable}>
            <div className={`${styles.analyticsTableHead} ${styles.analyticsTableDocs}`}>
                <span>Document</span>
                <span>AI-origin analysis</span>
                <span>Est. coverage</span>
                <span>Confidence</span>
            </div>
            {files.map(file => {
                const record = aiRecord(file);
                const coverage = record.analysed && record.coverage != null ? `${Math.round(record.coverage)}%` : '—';
                const confidence = fileConfidence(file) || '—';
                return (
                    <button
                        key={file.id || fileName(file)}
                        type="button"
                        className={`${styles.analyticsTableRow} ${styles.analyticsTableDocs}`}
                        onClick={() => onOpenFile?.(file)}>
                        <span className={styles.analyticsDocNameCell}>
                            <VDRDocumentIcon file={file} extension={fileExtension(file)} className={styles.analyticsFileIcon} />
                            <span>{fileName(file)}</span>
                        </span>
                        <span className={aiLabelTone(file)}>{aiLabel(file)}</span>
                        <span>{coverage}</span>
                        <span className={confidenceTone(confidence)}>{confidence}</span>
                    </button>
                );
            })}
        </div>
    );
}

function SourceRow({ row, summary, onClick }) {
    return (
        <button type="button" className={`${styles.analyticsTableRow} ${styles.analyticsTableSource}`} onClick={onClick}>
            <span className={styles.analyticsScopeName}>{row.label}</span>
            <span>{summary.classification}</span>
            <span>{summary.coverage}</span>
            <span className={styles.analyticsCoverageCell}>
                <span>
                    <span className={styles.analyticsCoveragePrimary}>{summary.analysedCount}</span>
                    <span className={styles.analyticsCoverageMuted}> / {summary.totalCount} analysed</span>
                </span>
                {summary.confidence && summary.confidence !== '—' ? (
                    <span className={`${styles.analyticsCoverageConfidence} ${confidenceTone(summary.confidence)}`}>
                        {summary.confidence}
                    </span>
                ) : null}
            </span>
        </button>
    );
}

function DocumentDrill({ drill, onBack, onClose, onOpenFolder, onChain, onOpenFile }) {
    const nodes = buildFolderNodes(drill.files || []);
    const chain = drill.chain || [];
    const current = chain[chain.length - 1] || null;
    const childFolders = current ? current.children : nodes;
    const singleLeaf = !current && nodes.length === 1 && nodes[0].children.length === 0;
    const files = singleLeaf ? nodes[0].files : current?.files || [];
    const showFolders = childFolders.length > 0 && !singleLeaf;

    const drillSummary = scopeSummary(drill.files || []);

    return (
        <div className={styles.analyticsDrill}>
            <p className={styles.analyticsCrumbs}>
                <button type="button" onClick={onClose}>Project</button>
                <span className={styles.analyticsCrumbSep}>›</span>
                {chain.length ? (
                    <button type="button" onClick={() => onChain?.([])}>{drill.label}</button>
                ) : (
                    <span className={styles.analyticsCrumbCurrent}>{drill.label}</span>
                )}
                {chain.map((node, index) => (
                    <React.Fragment key={node.key || node.label}>
                        <span className={styles.analyticsCrumbSep}>›</span>
                        {index < chain.length - 1 ? (
                            <button type="button" onClick={() => onChain?.(chain.slice(0, index + 1))}>{node.label}</button>
                        ) : (
                            <span className={styles.analyticsCrumbCurrent}>{node.label}</span>
                        )}
                    </React.Fragment>
                ))}
            </p>
            <p className={styles.analyticsDrillTitle}>{current?.label || drill.label}</p>
            <AISummaryStrip summary={drillSummary} />
            {showFolders ? (
                <div className={`${styles.analyticsTable} ${styles.analyticsTableFolders}`}>
                    <div className={styles.analyticsTableHead}>
                        <span>{current ? 'Subfolder' : 'Folder'}</span>
                        <span>Documents</span>
                    </div>
                    {childFolders.map(folder => (
                        <button key={folder.key || folder.label} type="button" className={styles.analyticsTableRow} onClick={() => onOpenFolder(folder)}>
                            <span>{folder.label}</span>
                            <span>{nodeCount(folder)}</span>
                        </button>
                    ))}
                </div>
            ) : null}
            {files.length ? (
                <div className={styles.analyticsTable}>
                    <div className={`${styles.analyticsTableHead} ${styles.analyticsTableDocs}`}>
                        <span>Document</span>
                        <span>AI-origin analysis</span>
                        <span>Est. coverage</span>
                        <span>Confidence</span>
                    </div>
                    {files.map(file => {
                        const record = aiRecord(file);
                        const coverage = record.analysed && record.coverage != null ? `${Math.round(record.coverage)}%` : '—';
                        const confidence = fileConfidence(file) || '—';
                        return (
                            <button
                                key={file.id || fileName(file)}
                                type="button"
                                className={`${styles.analyticsTableRow} ${styles.analyticsTableDocs}`}
                                onClick={() => onOpenFile?.(file)}>
                                <span className={styles.analyticsDocNameCell}>
                                    <VDRDocumentIcon file={file} extension={fileExtension(file)} className={styles.analyticsFileIcon} />
                                    <span>{fileName(file)}</span>
                                </span>
                                <span className={aiLabelTone(file)}>{aiLabel(file)}</span>
                                <span>{coverage}</span>
                                <span className={confidenceTone(confidence)}>{confidence}</span>
                            </button>
                        );
                    })}
                </div>
            ) : null}
            {!showFolders && !files.length ? <p className={styles.analyticsEmpty}>No documents</p> : null}
            <button type="button" className={styles.textLink} onClick={onBack}>
                Back
            </button>
        </div>
    );
}

function KeywordPanel({ title, subtitle, files, onClose, onOpenFile, searchable }) {
    const [query, setQuery] = React.useState('');
    const shown = files.filter(file => !query.trim() || fileName(file).toLowerCase().includes(query.trim().toLowerCase()));
    return (
        <div className={styles.analyticsKeywordPanel}>
            <div className={styles.analyticsCardHead}>
                <div>
                    <h3 className={styles.analyticsCardTitle}>{title}</h3>
                    <p className={styles.analyticsCardHint}>{subtitle}</p>
                </div>
                <button type="button" className={styles.textLink} onClick={onClose} aria-label="Close">
                    ✕
                </button>
            </div>
            {searchable ? (
                <input className={styles.analyticsSearch} value={query} onChange={event => setQuery(event.target.value)} placeholder="Search documents" aria-label="Search documents" />
            ) : null}
            <p className={styles.analyticsKicker}>Documents</p>
            {shown.map(file => (
                <div key={file.id || fileName(file)} className={styles.analyticsKeywordDoc}>
                    <div>
                        <p className={styles.analyticsCardTitle}>{fileName(file)}</p>
                        {createdLabel(file) ? <p className={styles.analyticsCardHint}>{createdLabel(file)}</p> : null}
                        <p className={styles.analyticsCardHint}>{fileLocation(file)}</p>
                    </div>
                    <button type="button" className={styles.textLink} onClick={() => onOpenFile?.(file)}>
                        Open →
                    </button>
                </div>
            ))}
            {shown.length === 0 ? <p className={styles.analyticsEmpty}>No documents</p> : null}
        </div>
    );
}

export default VDRAnalyticsView;
